import WP_Animation from "./CA_Animation.js";

/**
 * The Animator is used to handle all Animations and call the animations animate and prepareForNextIntersecting functions
 *
 * @since 1.1.0
 *
 * @author Alexander Uhlemann
 */
export default class CA_Animator {
  /**
   * The constructor
   *
   * @since 1.1.0
   *
   * @author Alexander Uhlemann
   *
   * @param { string } projectPrefix
   * @param { [WP_Animation] } animations
   */
  constructor(animations) {
    this.animations = animations;
    this.prevScrollPos = undefined;
    this.scrollDirection = {
      up: false,
      down: false,
    };
  }

  /**
   * Loop through all animations, fetch the html elements the animation needs and create an observer for this animation
   *
   * @since 1.1.0
   *
   * @author Alexander Uhlemann
   */
  initialiseAnimations = () => {
    this.animations.forEach((animation) => {
      const elements = document.querySelectorAll("[data-ca-" + animation.animationType + "]");
      this.makeObserver(animation, elements);
    });
  };

  /**
   * Creates an observer for the animation and iterates through the html elements to start observing them
   *
   * @param { WP_Animation } animation
   * @param { NodeListOf<HTMLElement> } elements
   *
   * @returns { void }
   */
  makeObserver = (animation, elements) => {
    let observer = new IntersectionObserver((entries, observer) => this.callback(entries, observer, animation), animation.observerConfig);
    elements.forEach((element) => observer.observe(element));
  };

  /**
   * The observer callback for each animation
   *
   * @param { object } entries The observer entries
   * @param { object } observer The observer
   * @param { WP_Animation } animation The animation which should handle the callback
   */
  callback = (entries, observer, animation) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Determine the direction of the scroll
        const scrollDirection = entry.boundingClientRect.top < entry.rootBounds.top ? "down" : "up";

        // Call the animations animate function, to tell, its time to animate
        animation.animate(entry.target, scrollDirection);

        // Unobserve the element, if the animation wants to be unobserved after one intersecting
        if (typeof animation.config === "object" && typeof animation.config.stopAfterIntersecting === "boolean" && animation.config.stopAfterIntersecting) {
          observer.unobserve(entry.target);
        }
      } else if (!animation.config?.stopAfterIntersecting && animation.hasOwnProperty("prepareForNextIntersecting") && typeof animation.prepareForNextIntersecting === "function") {
        animation.prepareForNextIntersecting(entry.target, this.scrollDirection);
      }
    });
  };
}
